package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Reloadable
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.requestWorkspaceResource

/** Owns remembered state that must be discarded when its selected-workspace authority retires. */
@Composable
internal fun <T> rememberWorkspaceAuthorityState(
    context: WorkspaceRequestContext?,
    vararg stateKeys: Any?,
    factory: () -> T,
): T = remember(context?.authoritySubject, *stateKeys) { factory() }

/** Actual direct protected-read leaf shared by the settings routes that load one privileged resource. */
@Composable
internal fun <T> loadWorkspaceProtectedResource(
    context: WorkspaceRequestContext?,
    privilege: WorkspacePrivilege,
    requestKeys: List<Any?> = emptyList(),
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    transport: suspend (workspaceSlug: String) -> T,
): Async<T> = loadAsync(context?.authoritySubject, *requestKeys.toTypedArray()) {
    requestWorkspaceResource(
        context = context,
        privilege = privilege,
        currentContext = currentContext,
        transport = transport,
    )
}

/**
 * The same read, reloaded underneath what is already on screen.
 *
 * **Use this wherever a page bumps a counter after an action.** Passing that counter through
 * [requestKeys] makes it an identity key, and a changed key discards the value *before* the request
 * is sent — so approving one spend request blanked the whole approval queue to a skeleton and then
 * replayed every card's staggered entrance, on the button the reader had just pressed. Six settings
 * routes did this. The motion contract forbids it by name under "skeleton-to-content dimensions"
 * and "optimistic save/approve advancement", and the reason is not aesthetic: the reader loses
 * their place in a list they were working through.
 *
 * [reloadToken] is the counter. Keys stay for things that genuinely change *which* resource this is
 * — an agent id, a locale — and a reload is not one of those.
 *
 * The result carries `refreshError` separately from `state`, so a refresh that fails leaves the last
 * good answer on screen with a notice rather than replacing it with an empty page.
 */
@Composable
internal fun <T> reloadWorkspaceProtectedResource(
    context: WorkspaceRequestContext?,
    privilege: WorkspacePrivilege,
    reloadToken: Int,
    requestKeys: List<Any?> = emptyList(),
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    transport: suspend (workspaceSlug: String) -> T,
): Reloadable<T> = loadReloadable(
    context?.authoritySubject,
    *requestKeys.toTypedArray(),
    reloadToken = reloadToken,
) {
    requestWorkspaceResource(
        context = context,
        privilege = privilege,
        currentContext = currentContext,
        transport = transport,
    )
}
